import {
  Box,
  BoxProps,
  CircularProgress,
  Pagination,
  PaginationProps,
  Stack,
  StackProps,
  Table,
  Typography,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { ReactNode } from "react";

type TablePaginationProps = {
  children: ReactNode;
  containerProps?: StackProps;
  tableContainerProps?: BoxProps;
  paginationProps?: PaginationProps;
  showCircularProgress?: boolean;
  showEmtyText?: boolean;
  emtyText?: string;
  searchText?: string;
  hidePagination?: boolean;
  searchConditionAsNaturalLanguage?: ReactNode;
};

/** children: Shoud use the TableHead,TableRow,TableCell... component of the MUI  */

export const XTablePagination = (props: TablePaginationProps) => {
  const theme = useTheme();
  return (
    <Stack
      sx={{
        direction: "column",
        justifyContent: "space-between",
        overflow: "hidden",
        position: "relative",
        height: "100%",
        flexGrow: 1,
      }}
      {...props.containerProps}
    >
      {props.showCircularProgress && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 999,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
            }}
          >
            <CircularProgress sx={{ color: theme.palette.primary.main }} />
          </Box>
        </Box>
      )}
      {props.searchConditionAsNaturalLanguage != null && (
        <Stack direction={"row"} alignItems={"center"} padding={"10px 10px"}>
          {typeof props.searchConditionAsNaturalLanguage === "string" ? (
            <Typography
              component={"div"}
              sx={{ fontWeight: "bold", color: "blue", whiteSpace: "pre-line" }}
            >
              {props.searchConditionAsNaturalLanguage}
            </Typography>
          ) : (
            props.searchConditionAsNaturalLanguage
          )}
        </Stack>
      )}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          height: "100%",
        }}
        {...props.tableContainerProps}
      >
        {props.showEmtyText && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              position: "absolute",
              paddingTop: "15px",
              paddingBottom: "15px",
              top: "50%",
              width: "100%",
              textAlign: "center",
            }}
          >
            {props.showEmtyText && (
              <Typography>
                {props.emtyText ||
                  (props.searchText && `Không tìm thấy dữ liệu`)}
              </Typography>
            )}
          </Box>
        )}
        <Box border={`1px solid ${theme.palette.divider}`}>
          <Table
            stickyHeader
            sx={{
              ".MuiTableHead-root .MuiTableCell-root": {
                backgroundColor: theme.palette.background.default,
                textTransform: "capitalize",
              },
              ".MuiTableCell-root": {
                padding: "8px",
                border: `1px solid ${theme.palette.divider}`,
              },
              // borderCollapse: "collapse",
              borderRadius: "8px",
              ".MuiTableCell-stickyHeader": {
                zIndex: "9",
              },
              ".MuiTableBody-root": {
                ".MuiTableCell-root": {
                  backgroundColor: theme.palette.background.paper,
                },
              },
            }}
          >
            {props.children}
          </Table>
        </Box>
      </Box>
      <Stack
        flex={1}
        direction={"row"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        {!props.hidePagination && <XPagination {...props.paginationProps} />}
      </Stack>
    </Stack>
  );
};

const XPagination = observer(({ ...props }: PaginationProps) => {
  const { inProgress } = useStores();
  return (
    <Pagination
      {...props}
      sx={{
        ...props?.sx,
        display: "flex",
        justifyContent: "center",
        marginTop: "15px",
        marginBottom: "15px",
      }}
      disabled={inProgress || props.disabled}
      color="primary"
      showFirstButton
      showLastButton
    />
  );
});
